import { useRouter } from "next/router"
import { extractNumberFromQueryString } from "common/lib/queryString"
import { usePageProps } from "common/hooks/data/usePageProps"
import type { NewslettersPageProps } from "pages/newsletters/index.page"
import type { NewsletterGroup, NewsletterGroupItem } from "modules/newsletters/types"
import { useMemo } from "react"

/**
 * Returns newsletter groups from the page props, and the newsletter the `promoted` query
 * string names, if it's on the page.
 */
export const useNewsletterGroups = () => {
  const { query } = useRouter()
  const { groups: pageGroups } = usePageProps<NewslettersPageProps>()

  return useMemo(() => {
    if (!pageGroups.length) {
      return { groups: pageGroups, promotedNewsletter: undefined }
    }

    const groups = withThumbnailsOnlyIfAllHaveOne(pageGroups)

    /*
     * The `promoted` query string names a newsletter to lead the page with, so links from
     * campaigns and Member Services redirects can pre-select it. Without one, the page
     * keeps the order editors gave the groups.
     */
    const promotedIdQueryString = extractNumberFromQueryString(query.promoted)

    const promotedNewsletter: NewsletterGroupItem | undefined = groups
      .flatMap((group) => group.groupItems)
      .find((item) => item.id === promotedIdQueryString)

    return { groups, promotedNewsletter }
  }, [pageGroups, query])
}

/**
 * Editorial wants a page to show thumbnails only when every newsletter on it has one, so
 * this drops them all if any is missing. Cards render no thumbnail without a URL.
 */
const withThumbnailsOnlyIfAllHaveOne = (groups: NewsletterGroup[]): NewsletterGroup[] => {
  const allHaveThumbnails = groups
    .flatMap((group) => group.groupItems)
    .every((item) => item.thumbnailImageUrl)

  if (allHaveThumbnails) {
    return groups
  }

  return groups.map((group) => ({
    ...group,
    groupItems: group.groupItems.map((item) => ({ ...item, thumbnailImageUrl: null })),
  }))
}
